/**
 * Debounced smart-tag suggestions for a query.
 *
 * Given a (controlled) query string, returns the matching smart tags from the
 * TagForge service via the `/api/tags` proxy
 * Each query change is debounced, and only the newest request is allowed to
 * update state.
 *
 * Everything the UI reads (`status`, `matches`) is derived at render time from
 * the last completed request plus the current query, rather than written
 * synchronously inside the effect. The previous result is held on screen while
 * the next request runs so suggestions do not flicker off on every keystroke.
 */

import { useEffect, useRef, useState } from 'react';
import { fetchSmartTags } from 'data/smart-tags/fetcher';
import type { SmartTagRequest, TagMatch } from 'data/smart-tags/types';

export type SmartTagSuggestionsStatus = 'idle' | 'loading' | 'ready' | 'error';

const DEFAULT_DEBOUNCE_MS = 300;
const DEFAULT_MIN_QUERY_LENGTH = 3;
const DEFAULT_MAX_TAG_MATCHES = 3;

export interface UseSmartTagSuggestionsOptions {
  debounceMs?: number;
  minQueryLength?: number;
  maxTagMatches?: number;
  index?: string;
  simThreshold?: number;
}

export interface SmartTagSuggestions {
  matches: TagMatch[];
  status: SmartTagSuggestionsStatus;
}

/** The outcome of the most recent completed request, tagged with its query. */
interface ResultSnapshot {
  query: string;
  matches: TagMatch[];
  failed: boolean;
}

const EMPTY_SNAPSHOT: ResultSnapshot = { query: '', matches: [], failed: false };

export function useSmartTagSuggestions(
  query: string,
  options: UseSmartTagSuggestionsOptions = {}
): SmartTagSuggestions {
  const {
    debounceMs = DEFAULT_DEBOUNCE_MS,
    minQueryLength = DEFAULT_MIN_QUERY_LENGTH,
    maxTagMatches = DEFAULT_MAX_TAG_MATCHES,
    index,
    simThreshold,
  } = options;

  const [snapshot, setSnapshot] = useState<ResultSnapshot>(EMPTY_SNAPSHOT);

  // Bumped per request so a slow earlier response cannot overwrite a newer one.
  const requestIdRef = useRef(0);

  const trimmedQuery = query.trim();
  const isBelowThreshold = trimmedQuery.length < minQueryLength;
  const isSnapshotCurrent = snapshot.query === trimmedQuery;

  useEffect(() => {
    // Too short to search, or already resolved for this exact query: invalidate
    // anything in flight and let render derive idle/ready from the snapshot.
    if (isBelowThreshold || isSnapshotCurrent) {
      requestIdRef.current += 1;
      return;
    }

    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    const controller = new AbortController();

    const timeoutId = setTimeout(() => {
      const request: SmartTagRequest = {
        text: trimmedQuery,
        maxTagMatches,
        ...(index ? { index } : {}),
        ...(simThreshold !== undefined ? { simThreshold } : {}),
      };

      fetchSmartTags(request, controller.signal)
        .then(response => {
          if (requestId !== requestIdRef.current) {
            return;
          }
          setSnapshot({ query: trimmedQuery, matches: response.matches, failed: false });
        })
        .catch(() => {
          // Aborted or superseded requests are expected — ignore them.
          if (controller.signal.aborted || requestId !== requestIdRef.current) {
            return;
          }
          setSnapshot({ query: trimmedQuery, matches: [], failed: true });
        });
    }, debounceMs);

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [trimmedQuery, isBelowThreshold, isSnapshotCurrent, debounceMs, maxTagMatches, index, simThreshold]);

  let status: SmartTagSuggestionsStatus = 'ready';
  if (isBelowThreshold) {
    status = 'idle';
  } else if (!isSnapshotCurrent) {
    status = 'loading';
  } else if (snapshot.failed) {
    status = 'error';
  }

  // Held over from the last completed request while the next runs, so results do
  // not blink out on every keystroke. Cleared only when below the threshold.
  const matches = isBelowThreshold ? [] : snapshot.matches;

  return { matches, status };
}

export default useSmartTagSuggestions;
